import { mergeSxProps, Text } from "@clipboard-health/ui-components";
import { Box, type SxProps, type Theme } from "@mui/material";
import { type ReactElement, type ReactNode, useRef } from "react";

import {
  type CreditProgressBarMode,
  useProgressBarAnimation,
} from "../hooks/useProgressBarAnimation";
import {
  BAR_BORDER_RADIUS,
  BAR_HEIGHT,
  CIRCLE_BORDER_WIDTH,
  CIRCLE_OVERLAP,
  CIRCLE_SIZE,
  CREDIT_PROGRESS_BAR_CONFIG,
  type CreditType,
  ICON_SIZE,
  TOOLTIP_RESERVED_HEIGHT,
} from "./creditProgressBarConfig";
import { CreditProgressBarTooltip } from "./CreditProgressBarTooltip";
import { ShieldIcon } from "./ShieldIcon";
import { useTooltipPosition } from "./useTooltipPosition";

export interface CreditProgressBarProps {
  completedCount: number;
  requiredCount: number;
  label?: string;
  /**
   * Determines the color theme for the progress bar.
   * "cancellation" renders in blue, "lateness" renders in green.
   * @default "cancellation"
   */
  creditType?: CreditType;
  icon?: ReactNode;
  /**
   * Controls how the progress bar animates:
   * - 'fill': animates from empty → current progress (earning behavior)
   * - 'drain': starts pre-loaded (full) and animates right-to-left to current progress (spending behavior)
   * - 'static': no animation, renders immediately at current progress
   * @default "static"
   */
  mode?: CreditProgressBarMode;
  /**
   * Optional callback fired when the animation completes (both bar and icon).
   * Not called for "static" mode.
   */
  onAnimationComplete?: () => void;
  /**
   * Whether the shield icon plays a pop animation when the bar fills to 100%.
   * @default false
   */
  popIconOnFull?: boolean;
  /**
   * Whether to show the tooltip bubble above the progress bar.
   * The tooltip displays "X/Y" and fades in after the animation completes.
   * @default false
   */
  showTooltip?: boolean;
  /** Optional helper text displayed below the progress bar (e.g. "5/20 shifts complete"). */
  subtext?: string;
  sx?: SxProps<Theme>;
}
/**
 * Progress bar for emergency credit tracking.
 *
 * Renders a pill-shaped bar with a circular shield icon at the end.
 * The bar fills or drains based on the animation `mode` and shows
 * an optional tooltip and subtext for additional context.
 */
export function CreditProgressBar(props: CreditProgressBarProps): ReactElement {
  const {
    completedCount,
    requiredCount,
    label,
    creditType = "cancellation",
    mode = "static",
    icon,
    onAnimationComplete,
    popIconOnFull = false,
    showTooltip = false,
    subtext,
    sx,
  } = props;

  const colorConfig = CREDIT_PROGRESS_BAR_CONFIG[creditType];
  const {
    currentProgress,
    isCurrentProgressFull,
    isAnimationComplete,
    isIconLit,
    barTransitionProps,
    iconTransitionProps,
  } = useProgressBarAnimation({ completedCount, requiredCount, mode, onAnimationComplete });
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipLeftPx = useTooltipPosition({ containerRef, currentProgress, showTooltip });
  const tooltipVisible = showTooltip && isAnimationComplete;

  return (
    <Box
      ref={containerRef}
      sx={mergeSxProps(
        {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          position: "relative",
          paddingTop: showTooltip ? TOOLTIP_RESERVED_HEIGHT : 0,
        },
        sx
      )}
    >
      {showTooltip && (
        <CreditProgressBarTooltip
          completedCount={completedCount}
          requiredCount={requiredCount}
          leftPx={tooltipLeftPx}
          visible={tooltipVisible}
        />
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          position: "relative",
        }}
      >
        {/* Progress pill – the colored bar that fills/drains */}
        <Box
          role="progressbar"
          aria-valuenow={completedCount}
          aria-valuemin={0}
          aria-valuemax={requiredCount}
          aria-label={label ?? `${completedCount}/${requiredCount} shifts complete`}
          sx={(theme) => ({
            display: "flex",
            flex: 1,
            height: BAR_HEIGHT,
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            borderRadius: BAR_BORDER_RADIUS,
            overflow: "hidden",
            backgroundColor: theme.palette.background.tertiary,
            paddingLeft: 2,
            paddingRight: 4,
            zIndex: 0,
          })}
        >
          {/* Animated fill overlay – width transitions via barTransitionProps */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${currentProgress * 100}%`,
              background: colorConfig.filledBarBackground,
              borderRadius: BAR_BORDER_RADIUS,
              transitionProperty: "width",
              ...barTransitionProps,
            }}
          />
          {/*
           * Dual-layer label technique: the bottom layer (zIndex 1) shows
           * the default text color. The top layer (zIndex 2, clipped to the
           * filled portion) shows inverted text so it remains readable on
           * top of the colored fill.
           */}
          {label && (
            <Text
              semibold
              color={(theme) => theme.palette.text.primary}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                textAlign: "center",
                zIndex: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingX: 2,
              }}
              variant="caption"
            >
              {label}
            </Text>
          )}
          {/* Top layer – clipped to the filled region for contrast */}
          {label && (
            <Text
              aria-hidden
              semibold
              color={(theme) => theme.palette.text.invertPrimary}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                textAlign: "center",
                zIndex: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingX: 2,
                // Clip from the right to match the progress fill width
                clipPath: `inset(0 ${(1 - currentProgress) * 100}% 0 0)`,
                transitionProperty: "clip-path",
                ...barTransitionProps,
              }}
              variant="caption"
            >
              {label}
            </Text>
          )}
        </Box>
        {/* Circular shield icon – overlaps the bar end via negative margin */}
        <Box
          sx={(theme) => ({
            width: CIRCLE_SIZE,
            height: CIRCLE_SIZE,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            background: theme.palette.background.tertiary,
            // Subtle shadow only when full
            boxShadow: isCurrentProgressFull ? "0px 2px 4px rgba(0, 0, 0, 0.1)" : "none",
            // Border matches card background to create the visual gap
            border: `${CIRCLE_BORDER_WIDTH}px solid ${theme.palette.background.primary}`,
            // Overlap the pill; the remaining visible overlap is the two slivers
            // above/below the arc, covered by the circle at z-index 1.
            marginLeft: `-${CIRCLE_SIZE / 2 - CIRCLE_OVERLAP}px`,
            zIndex: 1,
            position: "relative",
            overflow: "hidden",
            ...(popIconOnFull && {
              "@keyframes popScale": {
                "0%": { transform: "scale(1)" },
                "50%": { transform: "scale(1.4)" },
                "100%": { transform: "scale(1)" },
              },
              animation: isCurrentProgressFull
                ? `popScale 400ms ease-out calc(${iconTransitionProps.transitionDelay} + ${iconTransitionProps.transitionDuration}) both`
                : "none",
            }),
            transitionProperty: "box-shadow",
            ...iconTransitionProps,
          })}
        >
          {/* Circle fill – revealed via clipPath when progress reaches 100% */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: colorConfig.filledCircleBackground,
              borderRadius: "50%",
              // Clip from right, revealing from left as animation progresses
              clipPath: isCurrentProgressFull ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
              transitionProperty: "clip-path",
              ...iconTransitionProps,
            }}
          />
          {icon ?? (
            <ShieldIcon
              variant="outlined"
              sx={(theme) => ({
                width: ICON_SIZE,
                height: ICON_SIZE,
                color: isIconLit ? "white" : theme.palette.border?.primary,
                position: "relative",
                zIndex: 1,
                transitionProperty: "color",
                transitionDuration: iconTransitionProps.transitionDuration,
                transitionTimingFunction: iconTransitionProps.transitionTimingFunction,
              })}
            />
          )}
        </Box>
      </Box>
      {subtext && (
        <Text
          semibold
          variant="caption"
          color={(theme) => theme.palette.text.tertiary}
          sx={{ marginTop: 1 }}
        >
          {subtext}
        </Text>
      )}
    </Box>
  );
}
